import Hint from "../shared/Hint.jsx";
import ThemedSelect from "../shared/ThemedSelect.jsx";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Dropdown, Form } from "react-bootstrap";
import { FaSlidersH } from "react-icons/fa";
import { updatePageDefaults } from "../../services/apiClient.js";
import { currentPageDefaults, clearSavedPageState } from "../../services/pageDefaults.js";

// "Page defaults" — the filters a page opens with on a FRESH login / new
// browser / cleared cache (Robert 2026-07-22). Only the seed: once the user
// changes a filter it's remembered per session and these no longer apply until
// the cache is cleared. Sits beside the theme picker in the user menu.
//
// Two behaviours worth noting (both Robert 2026-07-22):
//  * "My …" choices are NOT selectable for users outside the PCO list
//    (shown_in_project_coordinator, set in Manage Users) — the resolver would
//    fall back to All anyway; the panel just says so honestly.
//  * Saving while ON an affected page reloads that page so the new defaults
//    apply instantly (changing pages already picked them up).

// The pages whose remembered filters the defaults govern — saving while on one
// of these reloads it so the change is visible immediately.
const AFFECTED_PATHS = ["/claims/search", "/rdr/main", "/rdr/accounts", "/rdr/health-safety", "/rdr/workflow"];

function PageDefaultsMenu({ user, setCurrentUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  // "My …" options only make sense for someone in the PCO dropdown list.
  const canMe = !!user?.shown_in_project_coordinator;

  const [form, setForm] = useState(() => {
    const d = currentPageDefaults();
    return {
      // A stale stored "me" (user later un-flagged) displays as All.
      searchPco: canMe && d.search.pco === "me" ? "me" : "",
      searchSort: d.search.sort === "newest" ? "newest" : "attention",
      searchMore: d.search.more === "all" ? "all" : "default",
      rdrPm: canMe && ["me", "me+"].includes(d.rdr.pm) ? d.rdr.pm : "",
      rdrRecords: d.rdr.withRecordsOnly !== false,
      rdrMode: d.rdr.mode === "summary" ? "summary" : "detail",
    };
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const set = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setSaved(false);
  };

  async function save() {
    setSaving(true);
    try {
      const updated = await updatePageDefaults({
        search: { pco: form.searchPco, sort: form.searchSort, more: form.searchMore },
        rdr: { pm: form.rdrPm, withRecordsOnly: form.rdrRecords, mode: form.rdrMode },
      });
      setCurrentUser(updated); // refreshes the page-defaults cache via App
      // New defaults win immediately: forget the remembered Search/RDR filters
      // so the next visit seeds from them (in-session changes then persist
      // again as normal).
      clearSavedPageState();
      // Standing on an affected page? Reload it so the defaults show NOW.
      if (AFFECTED_PATHS.includes(location.pathname)) {
        navigate(0);
        return;
      }
      setSaved(true);
    } catch {
      /* leave the form as-is; a failed save just doesn't persist */
    } finally {
      setSaving(false);
    }
  }

  return (
    <Hint placement="bottom" label="Page defaults">
      <Dropdown autoClose="outside">
        <Dropdown.Toggle variant="outline-secondary" id="page-defaults-dropdown" aria-label="Page defaults" className="btn btn-sm no-caret">
          <FaSlidersH />
        </Dropdown.Toggle>
        {/* Keep option lists inside the panel so selections are not outside clicks. */}
        <Dropdown.Menu style={{ minWidth: 260 }} className="p-3">
          <div className="small text-muted mb-2">
            How pages open on a fresh login. Change a filter while you work and it's
            remembered instead.
          </div>

          <div className="fw-semibold small text-uppercase text-muted mb-1" style={{ letterSpacing: "0.05em" }}>
            Search page
          </div>
          <Form.Group className="mb-2">
            <Form.Label className="mb-1 small">Coordinator</Form.Label>
            <ThemedSelect menuPortalTarget={null} size="sm" value={form.searchPco} onChange={(e) => set({ searchPco: e.target.value })}>
              <option value="me" disabled={!canMe}>
                My jobs{canMe ? "" : " (not in the coordinator list)"}
              </option>
              <option value="">All jobs</option>
            </ThemedSelect>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label className="mb-1 small">Sort</Form.Label>
            <ThemedSelect menuPortalTarget={null} size="sm" value={form.searchSort} onChange={(e) => set({ searchSort: e.target.value })}>
              <option value="attention">Needs attention</option>
              <option value="newest">Newest first</option>
            </ThemedSelect>
          </Form.Group>
          {/* Whether the extra filters (Insurer, Assessor, dates, Options…) sit
              open or stay behind the More options button. Default is behind it:
              the row costs a chunk of the job list on a 1080p screen and only
              one person has asked to see it standing (Robert 2026-08-17), so it
              is her setting rather than everyone's layout. Unlike the two above
              this is a standing preference, not a first-visit seed — the
              expander state was never remembered per session. */}
          <Form.Group className="mb-3">
            <Form.Label className="mb-1 small">Options</Form.Label>
            <ThemedSelect menuPortalTarget={null} size="sm" value={form.searchMore} onChange={(e) => set({ searchMore: e.target.value })}>
              <option value="default">Show default</option>
              <option value="all">Show all options</option>
            </ThemedSelect>
          </Form.Group>

          {/* Always shown (Robert 2026-07-25): the PCO Dashboard is now visible
              to everyone regardless of the reports master switch, so its page
              defaults are no longer gated. */}
          <div className="fw-semibold small text-uppercase text-muted mb-1" style={{ letterSpacing: "0.05em" }}>
            Dashboard &amp; reports
          </div>
              <Form.Group className="mb-2">
                <Form.Label className="mb-1 small">Coordinator</Form.Label>
                <ThemedSelect menuPortalTarget={null} size="sm" value={form.rdrPm} onChange={(e) => set({ rdrPm: e.target.value })}>
                  <option value="me" disabled={!canMe}>
                    My jobs{canMe ? "" : " (not in the coordinator list)"}
                  </option>
                  <option value="me+" disabled={!canMe}>
                    My team{canMe ? "" : " (not in the coordinator list)"}
                  </option>
                  <option value="">Everyone</option>
                </ThemedSelect>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label className="mb-1 small">View</Form.Label>
                <ThemedSelect
                  menuPortalTarget={null}
                  size="sm"
                  value={form.rdrRecords ? "records" : "all"}
                  onChange={(e) => set({ rdrRecords: e.target.value === "records" })}
                >
                  <option value="records">Only with records</option>
                  <option value="all">All reports</option>
                </ThemedSelect>
              </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="mb-1 small">Layout</Form.Label>
            <ThemedSelect menuPortalTarget={null} size="sm" value={form.rdrMode} onChange={(e) => set({ rdrMode: e.target.value })}>
              <option value="detail">Detail</option>
              <option value="summary">Summary</option>
            </ThemedSelect>
          </Form.Group>

          <div className="d-flex align-items-center gap-2">
            <Button size="sm" variant="primary" onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save defaults"}
            </Button>
            {saved ? <span className="small text-success">Saved</span> : null}
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </Hint>
  );
}

export default PageDefaultsMenu;
